import type { MeFieldsFragment, MyIdentityFieldsFragment } from '@mbfab/client-api'
import { api } from '@mbfab/client-api'
import { isNull, isSome } from '@mbfab/shared'
import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import LogRocket from 'logrocket'
import platform from 'platform'
import { useEffect } from 'react'

import { CenteredLoading } from '@/components/app/centered-loading'
import { ErrorPanel } from '@/components/app/error-panel'
import { classifyGraphqlError } from '@/lib/apollo/error-classification'
import { setLastTenantId } from '@/lib/last-tenant'
import { Posthog } from '@/lib/monitoring/posthog'
import { setMonitoringUserInfo } from '@/lib/monitoring/user-info'
import { canAccessTenantApp } from '@/lib/tenant-access'
import { AuthenticatedApolloProvider } from '@/providers/authenticated-apollo-provider'
import { MeProvider } from '@/providers/me-context'

// v1 MemberLayout の isOnboardingPage と同じ。ガードの除外先とサイドバー非表示対象を一致させる
function isOnboardingPage(pathname: string, tenantId: string): boolean {
  return (
    pathname === `/t/${tenantId}/terms_of_use` ||
    pathname === `/t/${tenantId}/plan/confirm` ||
    pathname === `/t/${tenantId}/plan/complete`
  )
}

export const Route = createFileRoute('/t/$tenant_id')({
  component: Layout,
})

function Layout() {
  const { tenant_id: tenantId } = Route.useParams()

  return (
    <AuthenticatedApolloProvider tenantId={tenantId}>
      <MeGate />
    </AuthenticatedApolloProvider>
  )
}

function MeGate() {
  const navigate = useNavigate()
  const meQuery = api.useFetchMeQuery()
  // myIdentity はサイドバーのメニュー表示制御（admin ロール / 所属テナント数）に使う。
  // v1 の Sider も両方を引いており、ゲートで一度だけ引いて MeContext 経由で配る
  const identityQuery = api.useFetchMyIdentityQuery()

  const me = meQuery.data?.me
  const myIdentity = identityQuery.data?.myIdentity
  const hasData = isSome(me) && isSome(myIdentity)

  // 全画面ローディングは「まだ一度も取得できていない」ときだけにする。
  // refetch 中も出してしまうと配下の画面が丸ごとアンマウントされ、入力途中の内容や
  // 操作結果バナーが消える（組織設定の保存は refetchQueries で FetchMe を引き直すため、
  // 実際に保存成功バナーが表示されない不具合になっていた。
  // Apollo Client 4 は notifyOnNetworkStatusChange が既定 true で refetch ごとに loading が立つ）
  if (!hasData && (meQuery.loading || identityQuery.loading)) {
    return <CenteredLoading message="読み込み中..." />
  }

  const queryError = meQuery.error ?? identityQuery.error
  if (isSome(queryError)) {
    const classification = classifyGraphqlError(queryError)

    // 認証切れだけは hasData に関係なく必ず出す。
    // Apollo は同一クエリ・同一変数の refetch が失敗しても直前の data を保持するため、
    // hasData で抑制すると「一度でも表示できた後はセッションが切れても何も出ない」ことになり、
    // 画面は正常に見えるのに全操作が失敗し続ける無言状態になる。
    // このクライアントはマウント時のトークンを固定で持つので自動復帰もしない（再ログインが唯一の復帰手段）
    if (classification === 'unauthenticated') {
      return (
        <ErrorPanel
          title="ログインの有効期限が切れました"
          description="お手数ですが、もう一度ログインしてください。"
          action={{
            label: 'ログインし直す',
            onClick: () => {
              // /sign_in だと Auth0 のローカルセッションが生きている限り '/' へ即リダイレクトされ、
              // サーバーが同じトークンを拒否し続けるケースで堂々巡りになる。
              // /sign_out でセッションを確実に破棄してから再ログインさせる
              void navigate({ to: '/sign_out' })
            },
          }}
        />
      )
    }

    // 以降（権限エラー・その他）は、一度でも表示できているなら全画面エラーに差し替えない。
    // stale なデータのまま使い続けられる方が、操作結果ごと画面が吹き飛ぶより害が小さい
    if (isSome(me) && isSome(myIdentity)) {
      return <MeResolved me={me} myIdentity={myIdentity} />
    }

    // 「このテナントにはアクセスできません」は fetchMe（テナントスコープ）の拒否にしか当てはまらない。
    // fetchMyIdentity はテナント非依存なので、そちらの forbidden は下の汎用エラーに落とす
    if (classification === 'forbidden' && isSome(meQuery.error)) {
      return (
        <ErrorPanel
          title="このテナントにはアクセスできません"
          description="別のアカウントでログインするか、テナント選択に戻ってください。"
          action={{
            label: 'テナント選択に戻る',
            onClick: () => {
              void navigate({ to: '/' })
            },
          }}
        />
      )
    }

    return (
      <ErrorPanel
        title="エラーが発生しました"
        description="しばらくしてから再度お試しください。"
        action={{
          label: 'テナント選択に戻る',
          onClick: () => {
            void navigate({ to: '/' })
          },
        }}
      />
    )
  }

  if (isNull(me) || isNull(myIdentity)) {
    return <CenteredLoading message="読み込み中..." />
  }

  return <MeResolved me={me} myIdentity={myIdentity} />
}

function MeResolved({ me, myIdentity }: { me: MeFieldsFragment; myIdentity: MyIdentityFieldsFragment }) {
  const navigate = useNavigate()

  if (!canAccessTenantApp(me.role)) {
    return (
      <ErrorPanel
        title="権限がありません"
        description="このページにアクセスする権限がありません。別のアカウントでログインし直してください。"
        action={{
          label: 'ログアウト',
          onClick: () => {
            void navigate({ to: '/sign_out' })
          },
        }}
      />
    )
  }

  return <AuthorizedMeResolved me={me} myIdentity={myIdentity} />
}

function AuthorizedMeResolved({ me, myIdentity }: { me: MeFieldsFragment; myIdentity: MyIdentityFieldsFragment }) {
  const navigate = useNavigate()
  const pathname = useLocation({ select: (location) => location.pathname })
  const trackingProperties = {
    name: `${me.name} / ${me.tenant.name}`,
    email: me.email || 'unknown@buffup.jp',
    tenantId: me.tenant.id,
    tenantName: me.tenant.name,
    userId: me.id,
    userName: me.name,
    role: me.role,
    propertyConfiguredAt: dayjs().format('YYYY-MM-DD'),
    platform: platform.toString(),
  }

  useEffect(() => {
    setMonitoringUserInfo(me)
    Posthog.identify(me.id, trackingProperties)
    LogRocket.identify(me.id, { name: me.name, email: me.email, tenantName: me.tenant.name, role: me.role })
  }, [me.id, me.email, me.name, me.role, me.tenant.id, me.tenant.name])

  useEffect(() => {
    Posthog.trackAccess(pathname.replace(/\/t\/[^/]+/, '/t/[tenant_id]'), trackingProperties)
  }, [pathname, me.id, me.tenant.id])

  // 「前回利用」テナントの書き込みはここで一元化する（テナント切替画面のクリックだけでなく、
  // 所属1件の自動遷移・ブックマーク直行でも更新されるようにするため）
  useEffect(() => {
    setLastTenantId(me.tenant.id)
  }, [me.tenant.id])

  // preActive テナントを利用規約同意フローへ誘導する（v1 MemberLayout と同じ条件）。
  // navigate はレンダー中に呼ぶと警告になるため useEffect で副作用として実行する。
  // admin 新規テナントは現状 status=active 作成の暫定があるが、preActive 運用ではこのガードが必須
  const shouldRedirectToOnboarding =
    (me.role === 'manager' || me.role === 'admin') &&
    me.tenant.status === 'preActive' &&
    !isOnboardingPage(pathname, me.tenant.id)

  useEffect(() => {
    if (shouldRedirectToOnboarding) {
      void navigate({
        to: '/t/$tenant_id/terms_of_use',
        params: { tenant_id: me.tenant.id },
      })
    }
  }, [shouldRedirectToOnboarding, navigate, me.tenant.id])

  if (shouldRedirectToOnboarding) {
    return <CenteredLoading message="読み込み中..." />
  }

  return (
    <MeProvider me={me} myIdentity={myIdentity}>
      <Outlet />
    </MeProvider>
  )
}
